/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useFileUpload.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing file uploads (drag & drop, validation, previews).
    Handles both music files and chime files separately.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { validateFiles, createFilePreviewUrl, revokeFilePreviewUrl } from '../utils';

interface FileWithSize {
  name: string;
  path: string;
  size?: number;
}

/**
 * Hook for managing file uploads
 */
export function useFileUpload() {
  const { showError } = useNotifications();
  const { t } = useI18n();

  // Music files state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<Map<string, string>>(new Map());
  const [existingFiles, setExistingFiles] = useState<FileWithSize[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Chime files state
  const [selectedChimeFiles, setSelectedChimeFiles] = useState<File[]>([]);
  const [chimeFilePreviews, setChimeFilePreviews] = useState<Map<string, string>>(new Map());
  const [existingChimeFiles, setExistingChimeFiles] = useState<FileWithSize[]>([]);
  const chimeInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection (for music files)
  const handleFileSelect = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const { valid, errors } = validateFiles(fileArray);

      // Show errors
      errors.forEach((error) => showError(error));

      if (valid.length > 0) {
        const newPreviews = new Map<string, string>();
        valid.forEach((file) => {
          const url = createFilePreviewUrl(file);
          newPreviews.set(file.name, url);
        });

        setSelectedFiles((prev) => [...prev, ...valid]);
        setFilePreviews((prev) => {
          const merged = new Map(prev);
          newPreviews.forEach((url, name) => merged.set(name, url));
          return merged;
        });
      }
    },
    [showError],
  );

  // Handle chime file selection
  const handleChimeFileSelect = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const { valid, errors } = validateFiles(fileArray);

      // Show errors
      errors.forEach((error) => showError(error));

      if (valid.length > 0) {
        const newPreviews = new Map<string, string>();
        valid.forEach((file) => {
          const url = createFilePreviewUrl(file);
          newPreviews.set(file.name, url);
        });

        setSelectedChimeFiles((prev) => [...prev, ...valid]);
        setChimeFilePreviews((prev) => {
          const merged = new Map(prev);
          newPreviews.forEach((url, name) => merged.set(name, url));
          return merged;
        });
      }
    },
    [showError],
  );

  // Drag handlers
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect],
  );

  // Remove file
  const removeFile = useCallback(
    (fileName: string) => {
      setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
      const previewUrl = filePreviews.get(fileName);
      if (previewUrl) {
        revokeFilePreviewUrl(previewUrl);
        setFilePreviews((prev) => {
          const updated = new Map(prev);
          updated.delete(fileName);
          return updated;
        });
      }
    },
    [filePreviews],
  );

  // Remove existing file
  const removeExistingFile = useCallback((fileName: string) => {
    setExistingFiles((prev) => prev.filter((file) => file.name !== fileName));
  }, []);

  // Remove chime file
  const removeChimeFile = useCallback(
    (fileName: string) => {
      setSelectedChimeFiles((prev) => prev.filter((f) => f.name !== fileName));
      const previewUrl = chimeFilePreviews.get(fileName);
      if (previewUrl) {
        revokeFilePreviewUrl(previewUrl);
        setChimeFilePreviews((prev) => {
          const updated = new Map(prev);
          updated.delete(fileName);
          return updated;
        });
      }
    },
    [chimeFilePreviews],
  );

  // Remove existing chime file
  const removeExistingChimeFile = useCallback((fileName: string) => {
    setExistingChimeFiles((prev) => prev.filter((file) => file.name !== fileName));
  }, []);

  // Cleanup preview URLs on unmount
  const cleanup = useCallback(() => {
    filePreviews.forEach((url) => revokeFilePreviewUrl(url));
    chimeFilePreviews.forEach((url) => revokeFilePreviewUrl(url));
  }, [filePreviews, chimeFilePreviews]);

  return {
    // Music files
    selectedFiles,
    setSelectedFiles,
    filePreviews,
    existingFiles,
    setExistingFiles,
    dragActive,
    fileInputRef,
    handleFileSelect,
    handleDrag,
    handleDrop,
    removeFile,
    removeExistingFile,

    // Chime files
    selectedChimeFiles,
    setSelectedChimeFiles,
    chimeFilePreviews,
    existingChimeFiles,
    setExistingChimeFiles,
    chimeInputRef,
    handleChimeFileSelect,
    removeChimeFile,
    removeExistingChimeFile,

    // Cleanup
    cleanup,
  };
}

